import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { RootState } from '../../../../app/redux/configureStore';
import { getProfileDetails } from '../../../../app/redux/features/profile/getProfileDetails';
import { useAppDispatch, useAppSelector } from '../../../../app/redux/hooks';
import {
  GroupField,
  getEmptyFieldDetails,
  getProfileCompletionFields
} from '../../../../app/user/profile/profileUtils';
import { sortDataByWeight } from '../utils/sortDataByWeight';
import { transformProfile } from '../utils/transformProfile';

const useQuestionary = () => {
  const dispatch = useAppDispatch();
  const profileDetails = useAppSelector((state: RootState) => state.profile.profile);
  const { user } = useAppSelector((state: RootState) => state.auth);
  const profileCompletion = useAppSelector((state: RootState) => state.profileCompletion);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [unansweredQuestionsSorted, setUnansweredQuestionsSorted] = useState<GroupField[] | undefined>(undefined);

  useEffect(() => {
    if (user?.profileCompletion === 100) {
      setShowCompletionScreen(true);
    }
    if (isEmpty(profileDetails)) {
      dispatch(getProfileDetails());
    }
  }, []);

  useEffect(() => {
    const updatedProfile = transformProfile(profileDetails);

    const unansweredQuestions =
      getEmptyFieldDetails(user, updatedProfile, getProfileCompletionFields(profileDetails)) || [];
    const sortedQuestions = sortDataByWeight(unansweredQuestions);
    setUnansweredQuestionsSorted(sortedQuestions);
  }, [profileDetails]); // ensures it gets the unanswered questions only once

  const currentQuestion = unansweredQuestionsSorted?.[currentQuestionIndex];
  const isFirstQuestion = currentQuestionIndex === 0;
  const isLastQuestion = unansweredQuestionsSorted && currentQuestionIndex === unansweredQuestionsSorted.length - 1;

  const moveToNextQuestion = () => {
    if (isLastQuestion) {
      setShowCompletionScreen(true);
    }

    if (unansweredQuestionsSorted && currentQuestionIndex < unansweredQuestionsSorted.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const moveToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return {
    user: user,
    unansweredQuestions: unansweredQuestionsSorted,
    showCompletionScreen,
    isLoading: profileCompletion?.loading,
    completionPercentage: user?.profileCompletion,
    setShowCompletionScreen,
    isFirstQuestion,
    isLastQuestion,
    currentQuestionIndex,
    currentQuestion,
    moveToNextQuestion,
    moveToPreviousQuestion
  };
};

export default useQuestionary;
